import { SwapVert } from "@mui/icons-material";
import { Button, FormControl, FormLabel, Grid, Typography } from "@mui/material";
import { FC, useState } from "react";
import { RegionDto } from "../../dtos";
import { FormInput } from "../CoreLib/library";
import { IEntityAutocomplete } from "./IEntityAutocomplete";
import { AddressForm } from "./AddressForm";
import { AddressLookup } from "./AddressLookup";
import { SiteSelect } from "./SiteSelect";
import { Address } from "../../dtos/Address";

export interface IHaulToFromFormProps {
    handleHaulToAddressChange?: (address: Address) => void;
    handleHaulFromAddressChange?: (address: Address) => void;
    handleSwap?: (address1: Address, address2: Address) => void;
    enableLookup?: boolean;
    disabled?: boolean;
    haulToAddressLine1: string;
    haulToAddressLine2?: string;
    haulToCity: string;
    haulToState: string;
    haulToZipCode: string;
    haulToCountry: string;
    haulToDirections?: string;
    haulFromAddressLine1: string;
    haulFromAddressLine2?: string;
    haulFromCity: string;
    haulFromState: string;
    haulFromZipCode: string;
    haulFromCountry: string;
    haulFromDirections?: string;
    fieldErrors?: any;
    formDirection?: 'row' | 'column';
    haulToSiteName?: string;
    haulFromSiteName?: string;
    haulToRegion?: RegionDto;
    haulFromRegion?: RegionDto;
    handleHaulToSiteNameChange?: (value: string) => void;
    handleHaulFromSiteNameChange?: (value: string) => void;
    handleHaulToRegionChange?: (value: RegionDto) => void;
    handleHaulFromRegionChange?: (value: RegionDto) => void;
    handleHaulFromSiteLookup?: (address: Address) => void;
    handleHaulToSiteLookup?: (address: Address) => void;
    regions: RegionDto[];
};


export const HaulToFromForm: FC<IHaulToFromFormProps> = props => {
    const {
        enableLookup = false,
        regions,
        disabled,
        haulToAddressLine1,
        haulToAddressLine2,
        haulToCity,
        haulToState,
        haulToZipCode,
        haulToCountry,
        haulToDirections,
        haulFromAddressLine1,
        haulFromAddressLine2,
        haulFromCity,
        haulFromState,
        haulFromZipCode,
        haulFromCountry,
        haulFromDirections,
        fieldErrors,
        formDirection = 'row',
        haulToRegion,
        haulFromRegion,
        haulFromSiteName,
        haulToSiteName,
        handleHaulToAddressChange,
        handleHaulFromAddressChange,
        handleHaulFromSiteLookup,
        handleHaulToSiteLookup,
        handleHaulFromRegionChange,
        handleHaulFromSiteNameChange,
        handleHaulToRegionChange,
        handleHaulToSiteNameChange,
        handleSwap,
    } = props;

    const [haulToKey, setHaulToKey] = useState(0);
    const [haulFromKey, setHaulFromKey] = useState(0);

    const [fromSelectedSiteId, setFromSelectedSiteId] = useState('');
    const [toSelectedSiteId, setToSelectedSiteId] = useState('');


    return (
        <Grid item container direction='column'>
            <Grid item>
                <Typography> <strong>Haul From</strong></Typography>
            </Grid>
            <Grid item container direction='row' spacing={2}>
                {enableLookup && <Grid item xs={12} sm={4} md={3}>
                    <AddressLookup
                        handleAddressChange={handleHaulFromAddressChange}
                        key={haulFromKey}
                        disabled={disabled}
                    />
                </Grid>}
                {enableLookup && <Grid item xs={3}>
                    <SiteSelect
                        label="Site Lookup"
                        selectedSiteId={fromSelectedSiteId}
                        handleSelectedSiteChange={(site) => {
                            if (site) {
                                handleHaulFromSiteLookup?.({
                                    addressLine1: site.addressLine1,
                                    addressLine2: site.addressLine2,
                                    city: site.city,
                                    state: site.state,
                                    zipCode: site.zipCode,
                                    country: site.country,
                                    directions: site.directions,
                                    siteName: site.name,
                                    region: site.region
                                });
                            }
                            setFromSelectedSiteId(site?.id ?? '');
                        }}
                        disabled={disabled}
                    />
                </Grid>}
                <Grid item xs={3}>
                    <FormInput
                        value={haulFromSiteName}
                        onChange={(event) => {
                            handleHaulFromSiteNameChange?.(event.target.value);
                        }}
                        label='Site Name'
                        name='site'
                        errorText={fieldErrors?.get('site')}
                        fullWidth
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={3}>
                    <FormControl
                        fullWidth
                        disabled={disabled}>
                        <FormLabel>Region</FormLabel>
                        <IEntityAutocomplete
                            options={regions ?? []}
                            onChange={(_e, value) => { handleHaulFromRegionChange?.(value); }}
                            value={haulFromRegion}
                            getOptionLabel={(option: RegionDto) => option.name}
                            disabled={disabled}
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <Grid item container direction={formDirection} spacing={formDirection === 'row' ? 2 : 1} wrap="nowrap">
                <AddressForm
                    handleAddressChange={handleHaulFromAddressChange}
                    addressLine1={haulFromAddressLine1}
                    addressLine2={haulFromAddressLine2}
                    city={haulFromCity}
                    state={haulFromState}
                    zipCode={haulFromZipCode}
                    country={haulFromCountry}
                    directions={haulFromDirections}
                    fieldErrors={fieldErrors}
                    disabled={disabled}
                    enableLookup={false}
                    allOptional
                    enableAddressLine2={!disabled}
                />
            </Grid>
            <Grid item xs={12}>
                <Button
                    onClick={() => {
                        handleSwap?.({
                            addressLine1: haulFromAddressLine1,
                            addressLine2: haulFromAddressLine2,
                            city: haulFromCity,
                            state: haulFromState,
                            zipCode: haulFromZipCode,
                            country: haulFromCountry,
                            directions: haulFromDirections,
                            siteName: haulFromSiteName,
                            region: haulFromRegion,
                        }, {
                            addressLine1: haulToAddressLine1,
                            addressLine2: haulToAddressLine2,
                            city: haulToCity,
                            state: haulToState,
                            zipCode: haulToZipCode,
                            country: haulToCountry,
                            directions: haulToDirections,
                            siteName: haulToSiteName,
                            region: haulToRegion
                        });
                        setHaulToKey(key => key + 1);
                        setHaulFromKey(key => key + 1);
                        setFromSelectedSiteId('');
                        setToSelectedSiteId('');
                    }}
                    startIcon={<SwapVert />}
                    color='primary'
                    fullWidth
                    variant='contained'
                    disabled={disabled}
                    size='large'
                >
                    SWAP
                </Button>
            </Grid>
            <Grid item>
                <Typography sx={{ marginTop: 2 }}>
                    <strong>Haul To</strong>
                </Typography>
            </Grid>
            <Grid item container direction='row' spacing={2}>
                {enableLookup && <Grid item xs={12} sm={4} md={3}>
                    <AddressLookup
                        handleAddressChange={handleHaulToAddressChange}
                        key={haulToKey}
                        disabled={disabled}
                    />
                </Grid>}
                {enableLookup && <Grid item xs={3}>
                    <SiteSelect
                        label="Site Lookup"
                        selectedSiteId={toSelectedSiteId}
                        handleSelectedSiteChange={(site) => {
                            if (site) {
                                handleHaulToSiteLookup?.({
                                    addressLine1: site.addressLine1,
                                    addressLine2: site.addressLine2,
                                    city: site.city,
                                    state: site.state,
                                    zipCode: site.zipCode,
                                    country: site.country,
                                    directions: site.directions,
                                    siteName: site.name,
                                    region: site.region
                                });
                            }
                            setToSelectedSiteId(site?.id ?? '');
                        }}
                        disabled={disabled}
                    />
                </Grid>}
                <Grid item xs={3}>
                    <FormInput
                        value={haulToSiteName}
                        onChange={(event) => {
                            handleHaulToSiteNameChange?.(event.target.value);
                        }}
                        label='Site Name'
                        name='site'
                        errorText={fieldErrors?.get('site')}
                        fullWidth
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={3}>
                    <FormControl
                        fullWidth
                        disabled={disabled}>
                        <FormLabel>Region</FormLabel>
                        <IEntityAutocomplete
                            options={regions ?? []}
                            onChange={(_e, value) => { handleHaulToRegionChange?.(value); }}
                            value={haulToRegion}
                            getOptionLabel={(option: RegionDto) => option.name}
                            disabled={disabled}
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <Grid item container direction={formDirection} spacing={formDirection === 'row' ? 2 : 1} wrap="nowrap">
                <AddressForm
                    handleAddressChange={handleHaulToAddressChange}
                    addressLine1={haulToAddressLine1}
                    addressLine2={haulToAddressLine2}
                    city={haulToCity}
                    state={haulToState}
                    zipCode={haulToZipCode}
                    country={haulToCountry}
                    directions={haulToDirections}
                    fieldErrors={fieldErrors}
                    disabled={disabled}
                    enableLookup={false}
                    allOptional
                    enableAddressLine2={!disabled}
                />
            </Grid>
        </Grid>
    );
};